/**
 * components/ui/SchoolLogo.tsx
 * ------------------------------------------------------------------
 * Single logo component used on the sign-in page, the sidebar, and
 * anywhere the brand mark appears, so every surface matches.
 *
 * It tries the real logo files in order; the first that loads wins.
 * If none load it falls back to the original vector crest, so the
 * UI is never broken. Drop the real PNG at:
 *     public/images/lkc_logo.png   (preferred)
 * Vite serves public/ at the site root, so that file is reachable
 * at  /images/lkc_logo.png  — RESTART `npm run dev` after adding a
 * file to public/, and match the filename case exactly.
 * ------------------------------------------------------------------
 */
import { useState } from 'react';
import SchoolCrest from './SchoolCrest';

interface Props {
  size?: number;
  showMotto?: boolean;
  className?: string;
  /** Override the candidate list if your file is named differently. */
  sources?: string[];
}

const DEFAULT_SOURCES = [
  '/images/lkc_logo.png',
  '/images/lkc-logo.png',
  '/lkc_logo.png',
  '/lkc-logo.png',
];

export default function SchoolLogo({
  size = 56,
  showMotto = false,
  className = '',
  sources = DEFAULT_SOURCES,
}: Props) {
  const [idx, setIdx] = useState(0);
  const [exhausted, setExhausted] = useState(false);

  if (exhausted) {
    return (
      <SchoolCrest
        size={size}
        showMotto={showMotto}
        className={className}
      />
    );
  }

  return (
    <img
      src={sources[idx]}
      alt="Livingstone Kolobeng College"
      width={size}
      height={size}
      onError={() => {
        if (idx + 1 < sources.length) setIdx(idx + 1);
        else setExhausted(true);
      }}
      className={className}
      style={{ objectFit: 'contain', display: 'block' }}
    />
  );
}
